import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf';
import { PDFDocument } from 'pdf-lib';
import axios from 'axios';
import { Loading } from 'element-ui';

class PdfSignatureService {
  constructor() {
    // Set worker source path for pdf.js
    if (typeof window !== 'undefined' && 'Worker' in window) {
      GlobalWorkerOptions.workerSrc = require('pdfjs-dist/legacy/build/pdf.worker.entry');
    }
  }

  async verifySignature(file) {
    try {
      if (!file) throw new Error('Không có file được truyền vào');

      // Create FormData and append the file
      const formData = new FormData();
      formData.append('pdf_file', file);

      // Show loading state
      const loading = Loading.service({
        lock: true,
        text: 'Đang kiểm tra chữ ký số...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });

      try {
        // Make API call to extract signature using axios
        const response = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/co-op/degree/extract-signature`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        const result = response.data;
        
        if (!result.success) {
          throw new Error(result.message || 'Có lỗi xảy ra khi xác minh chữ ký số');
        }

        // Transform the API response to match the expected format
        const signatureInfo = result.data.signature_info;
        
        return {
          metadata: {
            title: result.data.file_name,
            author: signatureInfo.signer_name,
            creator: 'PDF Signed Document',
            creationDate: signatureInfo.signing_time,
            modificationDate: signatureInfo.signing_time
          },
          signatureInfo: [{
            name: 'Chữ ký số',
            signedBy: signatureInfo.signer_name,
            signedDate: signatureInfo.signing_time,
            reason: 'Xác thực văn bằng',
            location: signatureInfo.signer_dn,
            isValid: signatureInfo.signature_validation === 'Signature is Valid.',
            certificate: {
              issuer: signatureInfo.certificate_validation,
              subject: signatureInfo.signer_dn
            },
            hashAlgorithm: signatureInfo.hash_algorithm,
            signatureType: signatureInfo.signature_type,
            signedRanges: signatureInfo.signed_ranges,
            totalDocumentSigned: signatureInfo.total_document_signed
          }],
          isValid: signatureInfo.signature_validation === 'Signature is Valid.',
          totalPages: 1 // Since we don't have this info from the API
        };

      } finally {
        // Close loading state
        loading.close();
      }

    } catch (error) {
      console.error('Error verifying PDF signature:', error);
      throw new Error('Không thể xác minh chữ ký số. Vui lòng kiểm tra lại file PDF.');
    }
  }

  _showLoading() {
    return this.$loading({
      lock: true,
      text: 'Đang kiểm tra chữ ký số...',
      spinner: 'el-icon-loading',
      background: 'rgba(0, 0, 0, 0.7)'
    });
  }

  _detectEncoding(bytes) {
    if (bytes[0] === 0xFF && bytes[1] === 0xFE) return 'utf-16le';
    if (bytes[0] === 0xFE && bytes[1] === 0xFF) return 'utf-16be';
    return 'utf-8'; // mặc định
  }

  async _pdfsigExtractSignatures(arrayBuffer) {
    // Tương tự như cách pdfsig trích xuất thông tin
    try {
      const bytes = new Uint8Array(arrayBuffer);

      // Lấy phần đầu và cuối file
      const headLength = Math.min(bytes.length, 10000);
      const tailLength = Math.min(bytes.length, 30000);

      // Chuyển bytes sang string, dùng encoding latin1 để không bị lỗi ký tự
      const headString = this._bytesToString(bytes.slice(0, headLength), 'utf-8');
      const tailString = this._bytesToString(bytes.slice(bytes.length - tailLength), 'utf-8');

      const pdfString = headString + '\n' + tailString;

      // Tìm các đối tượng Sig và các đối tượng liên quan
      const sigObjects = [];
      const sigFieldObjects = [];

      // Trích xuất thông tin
      const nameMatch = pdfString.match(/\/Name\s*\((.*?)\)/);
      const name = nameMatch ? this._decodePdfString(nameMatch[1]) : 'Không xác định';

      const contactInfoMatch = pdfString.match(/\/ContactInfo\s*\((.*?)\)/);
      const contactInfo = contactInfoMatch ? this._decodePdfString(contactInfoMatch[1]) : 'Không xác định';

      const dateMatch = pdfString.match(/\/M\s*\((.*?)\)/);
      const date = dateMatch ? this._formatPdfDate(this._decodePdfString(dateMatch[1])) : 'Không xác định';

      const reasonMatch = pdfString.match(/\/Reason\s*\((.*?)\)/);
      const reason = reasonMatch ? this._decodePdfString(reasonMatch[1]) : 'Không xác định';

      const locationMatch = pdfString.match(/\/Location\s*\((.*?)\)/);
      const location = locationMatch ? this._decodePdfString(locationMatch[1]) : 'Không xác định';

      const subFilterMatch = pdfString.match(/\/SubFilter\s*\/(.*?)\s/);
      const subFilter = subFilterMatch ? subFilterMatch[1] : 'Không xác định';

      // Thêm vào danh sách
      if (contactInfo !== 'Không xác định') {
        sigObjects.push({
          objNumber: 1,
          name,
          signedBy: contactInfo || name,
          signedDate: date,
          reason,
          location,
          subFilter
        });
      }

      return {
        signatures: sigObjects
      };
    } catch (error) {
      console.error('Error in pdfsig extraction:', error);
      return { signatures: [] };
    }
  }

  _bytesToString(bytes, encoding = 'utf-8') {
    if (!(bytes instanceof Uint8Array)) {
      bytes = new Uint8Array(bytes);
    }

    if (typeof TextDecoder !== 'undefined') {
      try {
        return new TextDecoder(encoding).decode(bytes);
      } catch (e) {
        console.warn('TextDecoder error with encoding', encoding, e);
      }
    }

    // Fallback nếu không hỗ trợ TextDecoder
    let result = '';
    for (let i = 0; i < bytes.length; i += 2) {
      const code = bytes[i] + (bytes[i + 1] << 8);
      result += String.fromCharCode(code);
    }
    return result;
  }


  _decodePdfString(input) {
    // 1. Chuyển từng char code ra 2 byte (low, high)
    const bytes = [];
    for (let i = 0; i < input.length; i++) {
      const code = input.charCodeAt(i);
      bytes.push(code & 0xff);       // low byte
      bytes.push((code >> 8) & 0xff); // high byte
    }

    // 2. Decode lại bytes với utf-16le
    const decoder = new TextDecoder('utf-16le');
    const decoded = decoder.decode(Uint8Array.from(bytes));

    // 3. Loại bỏ các ký tự điều khiển thừa (nếu cần)
    return decoded.replace(/[\u0000-\u001F]+/g, '').trim();
  }

  async _readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      if (!(file instanceof File)) {
        reject(new Error('Đối tượng đầu vào không phải File'));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        // Đảm bảo trả về ArrayBuffer thực sự
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result);
        } else {
          reject(new Error('FileReader không trả về ArrayBuffer'));
        }
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  }

  async _extractPdfInfo(arrayBuffer) {
    try {
      // Sử dụng pdf.js để đọc metadata
      const pdf = await getDocument({ data: arrayBuffer }).promise;
      const metadata = await pdf.getMetadata();

      return {
        numPages: pdf.numPages,
        metadata: {
          title: metadata?.info?.Title || 'Không có tiêu đề',
          author: metadata?.info?.Author || 'Không xác định',
          creator: metadata?.info?.Creator || 'Không xác định',
          creationDate: this._formatPdfDate(metadata?.info?.CreationDate),
          modificationDate: this._formatPdfDate(metadata?.info?.ModDate),
        }
      };
    } catch (error) {
      console.error('Error extracting PDF info:', error);
      return {
        numPages: 0,
        metadata: {
          title: 'Không có tiêu đề',
          author: 'Không xác định',
          creator: 'Không xác định',
          creationDate: 'Không xác định',
          modificationDate: 'Không xác định',
        }
      };
    }
  }

  async _checkForSignature(arrayBuffer) {
    try {
      // Tìm các signature marker phổ biến trong file PDF
      const bytes = new Uint8Array(arrayBuffer);
      const maxLength = Math.min(bytes.length, 10000); // Chỉ tìm trong 10KB đầu tiên
      let text = '';

      for (let i = 0; i < maxLength; i++) {
        text += String.fromCharCode(bytes[i]);
      }

      // Kiểm tra các pattern chữ ký phổ biến
      const hasAdsPdf = text.includes('/adbe.pkcs7.detached') || text.includes('/adbe.pkcs7.sha1');
      const hasEtsiMarker = text.includes('/ETSI.CAdES.detached') || text.includes('/ETSI.RFC3161');
      const hasSigDict = text.includes('/Type/Sig') || text.includes('/FT/Sig');
      const hasByteRange = text.includes('/ByteRange');

      if (hasAdsPdf || hasEtsiMarker || hasSigDict || hasByteRange) {
        // Tìm thông tin về chữ ký
        let name = 'Chữ ký số';
        let signedBy = 'Không xác định';

        // Tìm tên người ký
        const nameMatch = text.match(/\/Name\s*\((.*?)\)/);
        if (nameMatch && nameMatch[1]) {
          name = nameMatch[1];
          signedBy = nameMatch[1];
        }

        // Tìm thời gian ký
        const dateMatch = text.match(/\/M\s*\((.*?)\)/);
        let signedDate = 'Không xác định';
        if (dateMatch && dateMatch[1]) {
          signedDate = this._formatPdfDate(dateMatch[1]);
        }

        // Tìm lý do ký
        const reasonMatch = text.match(/\/Reason\s*\((.*?)\)/);
        const reason = reasonMatch && reasonMatch[1] ? reasonMatch[1] : 'Không xác định';

        // Tìm vị trí ký
        const locationMatch = text.match(/\/Location\s*\((.*?)\)/);
        const location = locationMatch && locationMatch[1] ? locationMatch[1] : 'Không xác định';

        return [{
          name,
          signedBy,
          signedDate,
          reason,
          location,
          isValid: true
        }];
      }

      return [];
    } catch (error) {
      console.error('Error checking for signature:', error);
      return [];
    }
  }

  async _extractSignatureInfo(arrayBuffer) {
    try {
      // Thử phương pháp đơn giản trước
      const simpleCheck = await this._checkForSignature(arrayBuffer);
      if (simpleCheck.length > 0) {
        return simpleCheck;
      }

      // Nếu không tìm thấy bằng phương pháp đơn giản, thử với pdf-lib
      const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      const signatureInfoList = [];

      // Lấy danh sách các dictionaries trong PDF
      const catalog = pdfDoc.context.lookup(pdfDoc.context.trailerInfo.Root);

      // Kiểm tra AcroForm xem có thông tin signature không
      if (catalog.has('AcroForm')) {
        const acroForm = pdfDoc.context.lookup(catalog.get('AcroForm'));

        // Kiểm tra các trường trong AcroForm
        if (acroForm.has('Fields')) {
          const fields = acroForm.get('Fields');

          if (Array.isArray(fields)) {
            for (const fieldRef of fields) {
              try {
                const field = pdfDoc.context.lookup(fieldRef);

                // Kiểm tra xem field có phải là signature không
                if (field.has('FT') && field.get('FT').toString() === '/Sig') {

                  // Lấy thông tin chữ ký
                  if (field.has('V')) {
                    const sigDict = pdfDoc.context.lookup(field.get('V'));
                    const signatureInfo = this._extractSignatureData(field, sigDict, pdfDoc.context);
                    if (signatureInfo) {
                      signatureInfoList.push(signatureInfo);
                    }
                  }
                }
              } catch (fieldError) {
                console.error('Error processing field:', fieldError);
              }
            }
          }
        }
      }

      // Nếu không tìm thấy trong AcroForm, thử tìm trong các trang
      if (signatureInfoList.length === 0) {
        // Duyệt qua tất cả các trang để tìm widget annotations
        const pages = catalog.get('Pages');
        if (pages) {
          const pagesDict = pdfDoc.context.lookup(pages);
          const kids = pagesDict.get('Kids');

          if (Array.isArray(kids)) {
            for (const pageRef of kids) {
              try {
                const page = pdfDoc.context.lookup(pageRef);

                if (page.has('Annots')) {
                  const annots = page.get('Annots');
                  if (Array.isArray(annots)) {
                    for (const annotRef of annots) {
                      const annot = pdfDoc.context.lookup(annotRef);

                      // Kiểm tra xem annotation có phải là widget của signature không
                      if (annot.has('FT') && annot.get('FT').toString() === '/Sig') {
                        if (annot.has('V')) {
                          const sigDict = pdfDoc.context.lookup(annot.get('V'));
                          const signatureInfo = this._extractSignatureData(annot, sigDict, pdfDoc.context);
                          if (signatureInfo) {
                            signatureInfoList.push(signatureInfo);
                          }
                        }
                      }
                    }
                  }
                }
              } catch (pageError) {
                console.error('Error processing page:', pageError);
              }
            }
          }
        }
      }

      // Nếu vẫn không tìm thấy, tìm kiếm trong toàn bộ PDF để tìm signature dictionaries
      if (signatureInfoList.length === 0) {
        const xref = pdfDoc.context.trailerInfo.XRef;
        if (xref) {
          // Duyệt qua tất cả các objects trong PDF
          for (let i = 0; i < xref.size; i++) {
            try {
              const obj = pdfDoc.context.lookup(i);
              if (obj && typeof obj === 'object' && obj.has && obj.has('Type') && obj.get('Type').toString() === '/Sig') {
                const signatureInfo = this._extractSignatureData(null, obj, pdfDoc.context);
                if (signatureInfo) {
                  signatureInfoList.push(signatureInfo);
                }
              }
            } catch (objError) {
              // Bỏ qua lỗi khi duyệt qua các objects
            }
          }
        }
      }

      return signatureInfoList.length > 0 ? signatureInfoList : [];
    } catch (error) {
      console.error('Error extracting signature info with pdf-lib:', error);

      // Trả về mảng rỗng để thông báo không tìm thấy chữ ký
      return [];
    }
  }

  _extractSignatureData(field, sigDict, context) {
    try {
      if (!sigDict) return null;

      // Thử trích xuất thông tin từ chữ ký
      const signatureInfo = {
        name: this._extractValue(sigDict, 'Name', context) || (field && this._extractValue(field, 'T', context)) || 'Không xác định',
        signedBy: this._extractValue(sigDict, 'Name', context) || 'Không xác định',
        signedDate: this._formatPdfDate(this._extractValue(sigDict, 'M', context)) || 'Không xác định',
        reason: this._extractValue(sigDict, 'Reason', context) || 'Không xác định',
        location: this._extractValue(sigDict, 'Location', context) || 'Không xác định'
      };

      // Kiểm tra thông tin chứng chỉ
      if (sigDict.has('Cert')) {
        const cert = context.lookup(sigDict.get('Cert'));
        if (cert) {
          signatureInfo.certificate = {
            issuer: this._extractValue(cert, 'Issuer', context) || 'Không xác định',
            subject: this._extractValue(cert, 'Subject', context) || 'Không xác định'
          };

          // Sử dụng subject như người ký nếu có
          if (signatureInfo.certificate.subject && signatureInfo.certificate.subject !== 'Không xác định') {
            signatureInfo.signedBy = signatureInfo.certificate.subject;
          }
        }
      }

      // Kiểm tra ByteRange để xác định chữ ký hợp lệ
      const hasByteRange = sigDict.has('ByteRange');
      signatureInfo.isValid = hasByteRange;

      return signatureInfo;
    } catch (error) {
      console.error('Error extracting signature data:', error);
      return null;
    }
  }

  _extractValue(dict, key, context) {
    try {
      if (!dict || !dict.has || !dict.has(key)) return null;

      const value = dict.get(key);

      // Các giá trị PDF có thể là literal hoặc hex string
      if (typeof value === 'string') {
        return value;
      } else if (value && value.toString) {
        // Xử lý chuỗi PDF có dạng /Name
        const strValue = value.toString();
        if (strValue.startsWith('/')) {
          return strValue.substring(1);
        }
        return strValue;
      } else if (value && context && context.lookup) {
        // Thử lookup đối tượng
        try {
          const obj = context.lookup(value);
          if (obj && obj.toString) {
            return obj.toString();
          }
        } catch (e) {
          // Bỏ qua
        }
      }

      return null;
    } catch (error) {
      console.error(`Error extracting value for ${key}:`, error);
      return null;
    }
  }

  _extractSignerFromFileName(buffer) {
    // Phương pháp dự phòng: cố gắng đoán người ký từ các byte đầu
    try {
      const bytes = new Uint8Array(buffer);
      const maxLength = Math.min(bytes.length, 1000);
      let text = '';

      for (let i = 0; i < maxLength; i++) {
        text += String.fromCharCode(bytes[i]);
      }

      // Tìm các pattern thường gặp trong PDF chứa tên
      const nameMatch = text.match(/\/Name\s*\((.*?)\)/);
      if (nameMatch && nameMatch[1]) {
        return nameMatch[1];
      }

      // Tìm các pattern thường gặp trong PDF chứa DocInfo
      const authorMatch = text.match(/\/Author\s*\((.*?)\)/);
      if (authorMatch && authorMatch[1]) {
        return authorMatch[1];
      }

      return 'Không xác định';
    } catch (error) {
      return 'Không xác định';
    }
  }

  _getCurrentDate() {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  _formatPdfDate(dateString) {
    if (!dateString) return 'Không xác định';
    try {
      // PDF date format: "D:YYYYMMDDHHmmSS'Z'"
      if (typeof dateString === 'string') {
        const match = dateString.match(/D:(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
        if (match) {
          const [, year, month, day, hour, minute, second] = match;
          return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
        }
        return dateString;
      }

      // Có thể là đối tượng Date
      if (dateString instanceof Date) {
        const day = String(dateString.getDate()).padStart(2, '0');
        const month = String(dateString.getMonth() + 1).padStart(2, '0');
        const year = dateString.getFullYear();
        const hours = String(dateString.getHours()).padStart(2, '0');
        const minutes = String(dateString.getMinutes()).padStart(2, '0');
        const seconds = String(dateString.getSeconds()).padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
      }

      return 'Không xác định';
    } catch (error) {
      return 'Không xác định';
    }
  }
}

export default new PdfSignatureService(); 